import React, { useRef } from 'react'

import { Box, Stack, Typography, alpha, useTheme } from '@mui/material'
import { ArrowLeftIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { PAGE_GUTTER, PAGE_GUTTER_MOBILE } from 'constants/layout'
import { useScrolledPast } from 'hooks/useScrolledPast'
import { space } from 'settings/tokens'
import { glassSx } from 'utils/glass'

/**
 * Height of the header's PINNED portion — the back row plus its padding.
 *
 * A sub-view's pinned block is header + toolbar stacked, so the toolbar has to
 * stick BELOW this rather than at 0, or the two occupy the same 0px and the
 * toolbar slides under the header. Exported so the offset is one number both
 * sides read, instead of a literal repeated at the call site.
 *
 * `space.lg` (pt) + 40 (puck) + 8 (pb when sticky) = 64.
 *
 * Derived, not chosen — if the sticky branch's `pt` moves, this moves with it or
 * the sub-view toolbar pins into the header. It moved 12 -> 16 when the sub-view
 * header was aligned to `NavigationHeader`; see that `pt`.
 */
export const PAGE_HEADER_STICKY_HEIGHT = space.lg + 40 + 8

export type PageHeaderProps = {
  /** Page title, rendered as the page's single h2. */
  title: string
  /**
   * One or two sentences describing the page as a whole — scope, caveats,
   * "what this can't do". Sits directly under the title, never at the page
   * bottom (working-conventions §38).
   */
  description?: string
  /**
   * A short live VALUE shown on the title's own row, right-aligned — a count, a
   * status, a total. Not prose.
   *
   * Separate from `description` on purpose. `description` is static copy that
   * qualifies the page (§40); this changes as the data does, and putting a
   * number that reflows into the description slot would move the copy beneath it
   * every time the value changed. The title row is also where a count reads AS a
   * count rather than as part of a sentence.
   *
   * Added for the Activity feed's "N events", which had been living in the
   * toolbar as the left half of a space-between row — so removing the toolbar's
   * text label took the count with it unless it had somewhere to go.
   */
  titleMeta?: React.ReactNode
  /** Back-link label, e.g. "My certificates". Omit to render no back-link. */
  backLabel?: string
  /** Route the back-link navigates to. Required when `backLabel` is set. */
  backTo?: string
  /**
   * Overrides the plain `navigate(backTo)`. A page that animates its own exit
   * has to stay mounted while the animation plays and route when it finishes,
   * so it owns the timing — see `utils/pageTransition`, and the certificate
   * sub-views that use it.
   */
  onBack?: () => void
  /**
   * Pin the header, and give it the app's glass once it is stuck.
   *
   * For the certificate's sub-views (Provenance / Scans / Activity), where the
   * bottom nav is HIDDEN and the back-link is therefore the only way out — so
   * it has to stay reachable from wherever you have scrolled to. That is the
   * opposite of the Activity TAB, where the nav is present the whole time and a
   * pinned title would be chrome duplicating it.
   *
   * Off by default: a page that scrolls under a pinned header should opt in,
   * not inherit it.
   */
  sticky?: boolean
}

/**
 * The canonical page header for `apps/app`: back-link → title → description.
 *
 * Exists so the spacing rhythm is defined ONCE instead of being re-tuned per
 * page (working-conventions §36/§37). Every gap below is a deliberate value —
 * change it here and every page moves together.
 *
 * The gaps below are VISUAL distances measured in the browser at 375x812, not
 * the CSS values that produce them. That distinction is the whole point:
 * line-height adds leading above and below every line, and the back-link's 40px
 * tap target is taller than the text inside it, so the same `pt`/`mb` reads as a
 * different gap depending on the neighbours. State the BASIS with any number
 * here — an unqualified "12px" is how this block previously disagreed with
 * itself.
 *
 *  - page top → back-link TEXT ... 20px sticky / 21.9px static
 *                                    (`pt` is 12px; the text sits 8px (sticky)
 *                                     or ~9.9px (static) below the puck's top
 *                                     edge, because a 40px control centres a
 *                                     24px / 20.3px text run. That is why the
 *                                     two branches differ by ~2px off one `pt`.
 *                                     Owned HERE as of VERA-315 — it was
 *                                     `pt: {xs:4}` = 32px set independently by
 *                                     three PAGE files, so this component owned
 *                                     every gap in the block EXCEPT the one that
 *                                     opens it. Trimmed 16px → 12px on
 *                                     2026-08-23; `PAGE_HEADER_STICKY_HEIGHT`
 *                                     tracks this value and must move with it)
 *  - back-link text → title ...... 16px sticky / 25.9px static — UNCHANGED
 *                                    (from `pb: 1` / `pb: 2`, i.e. 8px / 16px:
 *                                     `apps/app` is on MUI's default 8px base,
 *                                     NOT the 4px `@repo/ui` base — an earlier
 *                                     note here called `pb: 2` "8px" and read
 *                                     the wrong scale. The rest of the distance
 *                                     is the puck hanging below its own label.
 *                                     §39 fixes this gap; do not retune it here.
 *                                     Note the two branches are NOT equal, which
 *                                     §39's single "12px" does not describe —
 *                                     see the report on that convention rather
 *                                     than "fixing" it locally)
 *  - title → description ......... 16px  (attached to the title, not floating.
 *                                     Was 2px before this component existed,
 *                                     which read as cramped)
 *  - title/description → content . 20px box-to-box. The distance to the first
 *                                     VISIBLE control is larger by whatever the
 *                                     content's own container pads itself, and
 *                                     that differs per surface: the certificate
 *                                     Activity SUB-VIEW's toolbar adds 9.5px
 *                                     (→ 29.5px to the search field), the
 *                                     Activity TAB's adds 5px (→ 25px). Trimmed
 *                                     24px → 20px on 2026-08-23.
 *
 *                                     The reference is ~29px, a section
 *                                     heading's gap to its first row ("Scans" →
 *                                     "Scan alerts"), so the header sits in the
 *                                     same rhythm as the sections below it. Be
 *                                     honest about the trade: no single `mb`
 *                                     puts BOTH surfaces on 29, because their
 *                                     toolbars differ by 4.5px of their own
 *                                     padding. At 24px it was sub-view 33.5 /
 *                                     tab 29; at 20px it is sub-view 29.5 / tab
 *                                     25. The sub-view is the surface this was
 *                                     tuned for — it is the one with the
 *                                     back-link → title → search stack. If the
 *                                     tab's 25px ever reads tight, close it in
 *                                     the TAB's toolbar padding, not here, or
 *                                     the sub-view drifts straight back off
 *
 * If you change `pt`/`mt`/`mb` here, re-measure rather than assuming the visual
 * gap moves by the same amount — it does not.
 */
export const PageHeader = ({
  title,
  description,
  titleMeta,
  backLabel,
  backTo,
  onBack,
  sticky = false,
}: PageHeaderProps) => {
  const theme = useTheme()
  const navigate = useNavigate()

  /*
   * A scroll read rather than an IntersectionObserver sentinel.
   *
   * `useScrolledPast` already backs the certificate page's own solid-header
   * flip, so this is the mechanism this app has rather than a third one — and
   * it does not depend on IO, which was measured NOT firing in the dev preview
   * pane (a sentinel-based version would look permanently unstuck there while
   * being fine in a real browser, i.e. unverifiable exactly where it is
   * verified).
   */
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const isStuck = useScrolledPast(sentinelRef, 0) && sticky

  return (
    <>
      {/*
        The sentinel sits in normal flow BEFORE the sticky box — never inside it.
        
        Inside, it is part of the pinned element: it travels with the bar, its
        rect never crosses the top edge, and `isStuck` can never become true. The
        bar then pins correctly but stays transparent forever, which is exactly
        how it shipped — rows scrolling visibly through a bar that was supposed
        to be glass. As a preceding sibling it scrolls away normally and crosses
        the edge at precisely the moment the bar pins.
      */}
      {/*
        `mb: -1px` cancels the sentinel's own height from the layout while leaving
        it exactly where it is in flow.

        It is 1px rather than 0 because a zero-height element is a zero-AREA
        target and its position cannot be read reliably — but that 1px is real
        layout, and it pushed the whole header down by one, which is precisely the
        amount by which a sub-view's back arrow then missed the certificate
        page's (MEASURED: 25 against 24 once the gutters matched). Negative margin
        removes the cost without moving the sentinel, so the scroll read is
        unaffected.
      */}
      {sticky && (
        <Box ref={sentinelRef} aria-hidden sx={{ height: '1px', mb: '-1px' }} />
      )}
      <Box
      sx={
        sticky
          ? {
              /*
               * `space.lg` — THE SAME TOP PADDING `NavigationHeader` USES, so a
               * sub-view's back arrow lands exactly where the certificate page's
               * does. That page pads its control row by `space.lg` and its 40px
               * control insets a 24px glyph by 8, putting the glyph at 24; this
               * header now resolves to the same 16 + 8.
               *
               * It was 12, and the caller then added its own top gutter on top,
               * so the arrow sat 21px LOWER than the one it had just been tapped
               * from (MEASURED: 45 against 24). The caller's gutter is gone — see
               * `CertificateSubView` — and this is the only remaining top gap.
               *
               * FIXED, not the responsive pair the static header takes below:
               * `PAGE_HEADER_STICKY_HEIGHT` is a compile-time constant the
               * sub-view toolbar pins against, and a breakpoint here would make
               * the pinned bar taller at `md` than the offset the toolbar was
               * told to use — they would overlap at exactly the width nobody
               * tests on a mobile-first app. Flat also happens to be what parity
               * requires, since `NavigationHeader`'s own padding is flat.
               *
               * Change this and change the constant, in the same edit.
               */
              pt: `${space.lg}px`,
              position: 'sticky',
              top: 0,
              // Page chrome, under MUI's drawer level so every bottom sheet
              // stays above it — the same layer the other pinned surfaces take.
              zIndex: 3,
              /*
               * Full-bleed to the COLUMN's edge while pinned. Without cancelling
               * the container's own padding the glass stops short of both edges
               * and rows slide past in the gutters.
               *
               * Read from `constants/layout` rather than hardcoded, because the
               * gutter it is cancelling is no longer the same at both
               * breakpoints: `PageContainer` is 16px at `xs` and `PAGE_GUTTER`
               * (24) at `md`. A fixed `-16px` was correct while the only caller
               * was a `p: 2` shell, and would have left an 8px sliver of unglassed
               * page down each side at `md` the moment that shell adopted the
               * shared column — a bug that only appears at desktop width, on a
               * mobile-first app, i.e. exactly the kind nobody sees.
               */
              mx: { xs: `-${PAGE_GUTTER_MOBILE}px`, md: `-${PAGE_GUTTER}px` },
              px: { xs: `${PAGE_GUTTER_MOBILE}px`, md: `${PAGE_GUTTER}px` },
              ...glassSx(theme, isStuck),
              /*
               * NO border here. The pinned block on a sub-view is header +
               * toolbar, and the hairline belongs at the BOTTOM of that block —
               * under the search row — exactly as HeaderBar draws it on the
               * certificates page. A border under the title drew a line through
               * the middle of one pinned surface.
               *
               * (It also painted at rest regardless of `isStuck`, because
               * `#vera-shadow-root * { border-color }` in index.css wins on ID
               * specificity over an sx value. That is why SearchFilterSortBar
               * sets its own border colour inline — see its note.)
               */

            }
          : /*
             * The static header's top padding, on the SAME element so a page
             * cannot set its own (three of them did, at 32px). Responsive is
             * kept, not dropped: desktop still gets more air than a phone. The
             * pages' original 4:6 became 2:3, then 1.5:2.5 in the 2026-08-23
             * trim — the ratio flattens slightly as the values shrink, because
             * holding 3:5 exactly would put `md` back where it started.
             *
             * This Box renders even with no back-link (Activity, Notifications
             * have none), where it is an empty element whose only job is this
             * padding. That is deliberate: the alternative is putting the page
             * top gap on the title Stack in one branch and here in the other,
             * which is two owners again.
             */
            { pt: { xs: 1.5, md: 2.5 } }
      }
    >
      {backLabel && (backTo || onBack) && (
        <Box
          role='button'
          tabIndex={0}
          aria-label={`Back to ${backLabel}`}
          onClick={() => (onBack ? onBack() : backTo && navigate(backTo))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            /*
             * NO PULL. The 40px control's BOX sits on the content column's left
             * edge, which puts the GLYPH at `column + (40 - 24) / 2` = column + 8
             * — exactly where the certificate page's back arrow sits, because
             * `NavigationHeader` aligns its box to the column in the same way.
             * MEASURED: both glyphs at 584 with the column at 576.
             *
             * ⚠️ This used to pull `-10px` (then `-8px` when the glyph grew to
             * 24) so the GLYPH rather than the box lined up with the title
             * beneath it. That is a defensible rule on its own — and it is the
             * one this file used to argue for — but it cannot be true at the same
             * time as arrow-to-arrow parity with the certificate page, since that
             * page aligns the BOX and is the surface these are reached from. The
             * user's call was parity: "force the x-match — make them line up".
             *
             * The cost, stated: the arrow now sits 8px inboard of the title below
             * it. The certificate page has the same 8px relationship to its own
             * content column, so the two surfaces are consistent with each other,
             * which is the property that was asked for.
             */
            ml: 0,
            /*
             * 8px sticky / 16px static, on `apps/app`'s 8px base. The rendered
             * back-text→title gap is LARGER than either — 16px and 25.9px
             * measured — because the 40px puck hangs below its own label and
             * that overhang is part of the gap the eye reads.
             *
             * §39 fixes this gap, so it is deliberately NOT retuned here; the
             * 2026-08-23 trim moved only the padding ABOVE the back row and the
             * margin BELOW the title.
             */
            pb: sticky ? 1 : 2,
            width: 'fit-content',
            cursor: 'pointer',
            color: theme.palette.text.primary,
            // The whole row is the target, and it dims as a unit rather than
            // lighting up a chip inside itself.
            transition: 'opacity 150ms ease',
            '&:hover': { opacity: 0.6 },
            '&:focus-visible': {
              outline: 'none',
              borderRadius: `${theme.shape.borderRadius}px`,
              boxShadow: `0 0 0 3px ${alpha(theme.palette.text.primary, 0.12)}`,
            },
          }}
        >
          {/*
            A 40px TARGET, but no visible chip.
            
            This went through both extremes. It started as an underlined text run
            — a ~16px tap target for the only way out of a sub-view, since the
            bottom nav is hidden here. It was then given the certificate page's
            filled puck, which was wrong in the other direction: that puck is
            filled because it floats on a PHOTOGRAPH and has to separate from it,
            and on a white page the same fill is a heavy grey box that competes
            with the title directly beneath it.
            
            What the row actually needs is the hit area, not the chrome. So the
            box keeps its 40px for the thumb and paints nothing — the arrow and
            the label read as one quiet breadcrumb, and the TITLE carries the
            weight, which is the hierarchy you want on a page whose identity is
            the title rather than its parent.
          */}
          <Box
            className='vera-back-puck'
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: `${theme.shape.borderRadius}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              /*
               * TRANSPARENT at rest. The certificate page's version is filled
               * because it floats on a photograph and needs to separate from it;
               * on a white page that same fill is a heavy grey box next to small
               * text, with nothing to separate from. The 40px target is the
               * point, not the chip.
               */
              backgroundColor: 'transparent',
              transition: 'background-color 150ms ease, box-shadow 150ms ease',
            }}
          >
            {/*
              24, matching the certificate page's back arrow exactly
              (`NavigationHeader` renders `ArrowLeft` at 24 inside the same 40px
              control). It was 20, so the same action rendered two different
              sizes one tap apart — the sub-views are reached FROM that page, so
              they are compared against it directly rather than in isolation.

              ⚠️ HORIZONTAL PARITY IS DELIBERATELY NOT TAKEN ALL THE WAY, and it
              is worth knowing why before "fixing" the last 8px. Three things
              want to be true and only two can be:

                a. the glyph sits on the content column's left edge, aligned with
                   the title beneath it — what the `ml` pull above buys here;
                b. the 40px control's BOX sits on that edge — which is what the
                   certificate page does, correctly, because ITS puck is FILLED
                   and floats on a photograph, so the visible object is the chip,
                   not the glyph, and a chip hanging outside the content column
                   is exactly what `NavigationHeader`'s `desktopInset` avoids;
                c. the two pages' arrows land on the same x.

              MEASURED: this glyph at 576 (= its title), the certificate page's
              at 584 (its 40px box at 576). Taking (c) means giving up (a) here
              or (b) there, and both of those are visible as a misalignment
              between two elements ON ONE SCREEN, whereas the 8px is only visible
              by measuring across a navigation. So SIZE is matched and each
              surface keeps the alignment rule its own treatment requires.
            */}
            <ArrowLeftIcon size={24} />
          </Box>

          {/*
            The label CROSSFADES from the parent's name to this page's own title
            once the header pins.

            Scrolled down, "which page am I on" beats "what is above me" — the
            parent is one tap away and its name is the thing you just left. It
            is the same swap NavigationHeader makes on the certificate page.

            Both labels are rendered stacked in one grid cell rather than
            swapped, so the row's width is the wider of the two at all times and
            nothing reflows mid-transition. Only rendered stacked when `sticky`;
            a static header has nothing to cross to.
          */}
          <Box sx={{ display: 'grid', minWidth: 0 }}>
            <Typography
              variant='body3'
              sx={{
                gridArea: '1 / 1',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                // SECONDARY. The parent's name is context for the title below
                // it, not a second title — at full strength the two competed and
                // the smaller one read as the more important, because it came
                // first.
                color: theme.palette.text.secondary,
                // No underline: this is navigation chrome, and nothing else in
                // the app underlines its nav. It read as a hyperlink in prose.
                opacity: isStuck ? 0 : 1,
                transition: 'opacity 150ms ease',
              }}
            >
              {backLabel}
            </Typography>
            {sticky && (
              <Typography
                aria-hidden={!isStuck}
                sx={{
                  gridArea: '1 / 1',
                  fontSize: 16,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  opacity: isStuck ? 1 : 0,
                  transition: 'opacity 150ms ease',
                  pointerEvents: 'none',
                }}
              >
                {title}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      </Box>

      {/*
        The TITLE IS OUTSIDE THE PINNED BOX, and this is load-bearing rather than
        tidiness.

        A sticky element's height is its FLOW height, so anything inside the
        pinned box is pinned. With the title in there the header pinned at ~110px
        while the toolbar was pinning at PAGE_HEADER_STICKY_HEIGHT (64) — and the
        two overlapped, the title landing on top of the search field. The
        constant was not wrong; the structure it described was.

        Keeping the title in normal flow also keeps the pinned bar's height
        CONSTANT, which is what makes the offset a constant at all. If the pinned
        box could grow or shrink it would change the flow after it, shifting
        content and potentially feeding back into the stuck state.

        So: the compact bar pins, the big title scrolls away underneath it, and
        the bar's label crossfades to the title once it does — which is why
        nothing is lost by letting it go.
      */}
      {/*
        20px on `apps/app`'s 8px base — the gap from the header block down to the
        page's first content, and the LAST gap this component owns.

        Box-to-box only. The distance the eye reads is larger by the content
        container's own padding and so differs per surface; the measured table at
        the top of this file has both, and the reasoning for 20px over 24px.
      */}
      <Stack sx={{ mb: 2.5 }}>
        {/*
          The row is only introduced when there IS a meta value. A flex wrapper
          around the lone h2 would render the same today, but it turns the
          heading from a block into a shrink-to-fit flex item — a difference that
          costs nothing until some page's title needs to wrap, and then it wraps
          differently for no reason anyone would find. One element, one job.
        */}
        {titleMeta ? (
          <Stack
            direction='row'
            // BASELINE, not center: the meta is 14px against a 24px title, and
            // centering hangs it in the middle of the cap height where it reads
            // as floating. On the baseline it reads as sitting on the same line.
            alignItems='baseline'
            justifyContent='space-between'
            spacing={2}
          >
            <h2 className='text-2xl leading-8 font-normal'>{title}</h2>
            {titleMeta}
          </Stack>
        ) : (
          <h2 className='text-2xl leading-8 font-normal'>{title}</h2>
        )}
        {description && (
          <Typography
            variant='body3'
            sx={{ color: theme.palette.text.secondary, mt: 2, lineHeight: 1.5 }}
          >
            {description}
          </Typography>
        )}
      </Stack>
    </>
  )
}
